import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { BUILD_ID } from '../../buildInfo';
import { useEnvironment } from '../../utils/environment';
import { errorText } from '../../utils/errorText';
import { trapTab } from '../../utils/focusTrap';
import { focusElement } from '../../utils/useFocusOnOpen';
import {
  MAX_COMMENT, MAX_SCREENSHOTS, SCREENSHOT_TYPES, currentPage, screenshotProblem, submitIssueReport, viewportSize,
} from '../../utils/issueReports';

/*
 * "Something is wrong on this page": what the person saw, in their words, and
 * up to three screenshots (picked, or pasted from the clipboard). The page,
 * the build and the window size go with it; the server adds the environment
 * and the browser (routes/issue_reports.py). The administrators are emailed.
 *
 * A labelled modal dialog, as the hub's are: focus moves to its heading, Tab
 * stays inside, Escape and × close it -- except while sending, when nothing
 * closes it -- and focus goes back to whatever opened it.
 *
 * It opens over any other dialog (2026-10-15, opened from anywhere by
 * IssueReportProvider): drawn above them all (3600), and its keys are its
 * own -- Escape and Tab are taken in the window's capture phase, every other
 * key that starts inside it stops at it -- so the dialog beneath neither
 * closes, nor pulls Tab back, nor turns its pages under the person typing.
 * `context` names the screen it was opened on (utils/issueReports.js
 * screenContext), sent as the report's page_title.
 */
let nextId = 0;

/** Mounted only while open: each opening is a fresh form, and nothing (not
 *  even the environment's name) is asked for while it is closed. Drawn on
 *  <body>, not where its button is: the navbar's nowrap and stacking context
 *  must not reach it. */
export default function ReportIssueModal({ open, onClose, context = null }) {
  if (!open) return null;
  const dialog = <ReportIssueDialog onClose={onClose} context={context} />;
  return typeof document !== 'undefined' && document.body ? createPortal(dialog, document.body) : dialog;
}

function ReportIssueDialog({ onClose, context }) {
  const { t } = useTranslation('issues');
  const environment = useEnvironment();
  const [comment, setComment] = useState('');
  const [shots, setShots] = useState([]);           // [{id, file, url}]
  const [problems, setProblems] = useState([]);
  const [phase, setPhase] = useState('edit');       // edit | sending | done
  const [failed, setFailed] = useState([]);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const fileRef = useRef(null);
  const opener = useRef(null);
  const busy = phase === 'sending';
  const busyRef = useRef(false);
  busyRef.current = busy;
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  // Previews are object URLs: let them go when the dialog does.
  const shotsRef = useRef(shots);
  shotsRef.current = shots;
  useEffect(() => () => shotsRef.current.forEach((s) => s.url && URL.revokeObjectURL?.(s.url)), []);

  useEffect(() => {
    opener.current = typeof document !== 'undefined' ? document.activeElement : null;
    focusElement(headingRef.current);
    // Capture phase, on the window: before any other dialog's listener.
    const onKey = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (!busyRef.current) closeRef.current?.();
        return;
      }
      if (e.key === 'Tab') {
        e.stopPropagation();
        trapTab(dialogRef.current, e);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      const back = opener.current;
      if (back && typeof back.focus === 'function' && back.isConnected !== false) back.focus({ preventScroll: true });
    };
  }, []);

  const close = () => { if (!busy) onClose?.(); };

  const addFiles = (list) => {
    const found = [];
    const accepted = [];
    let room = MAX_SCREENSHOTS - shots.length;
    for (const file of list) {
      const problem = screenshotProblem(file);
      if (problem) { found.push(t(`problem.${problem}`, { name: file.name || t('pasted') })); continue; }
      if (room <= 0) { found.push(t('problem.tooMany', { max: MAX_SCREENSHOTS })); break; }
      room -= 1;
      nextId += 1;
      accepted.push({ id: `shot-${nextId}`, file,
        url: typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file) : null });
    }
    setProblems(found);
    if (accepted.length) setShots((s) => [...s, ...accepted]);
  };

  const onPick = (e) => {
    addFiles(Array.from(e.target.files || []));
    e.target.value = '';
  };

  // A screenshot pasted anywhere in the dialog joins the list; pasted text is
  // left alone.
  const onPaste = (e) => {
    const images = Array.from(e.clipboardData?.files || []).filter((f) => f.type?.startsWith('image/'));
    if (!images.length) return;
    e.preventDefault();
    addFiles(images);
  };

  const remove = (id) => setShots((s) => s.filter((x) => {
    if (x.id === id && x.url) URL.revokeObjectURL?.(x.url);
    return x.id !== id;
  }));

  const text = comment.trim();
  const canSend = phase === 'edit' && text.length > 0 && text.length <= MAX_COMMENT;
  const page = currentPage();

  const send = async () => {
    if (!canSend) return;
    setPhase('sending');
    setError('');
    try {
      const result = await submitIssueReport({
        comment: text, pagePath: page,
        pageTitle: context || (typeof document !== 'undefined' ? document.title : null),
        appVersion: BUILD_ID, viewport: viewportSize(), files: shots.map((s) => s.file),
      });
      setFailed(result.failed);
      setPhase('done');
      focusElement(headingRef.current);
    } catch (e) {
      setError(t('failed', { error: errorText(e) }));
      setPhase('edit');
    }
  };

  return (
    // The backdrop does not close it: a stray tap must not throw away what was typed.
    <div className="hub-drawer-backdrop" data-testid="report-issue-backdrop" data-report-issue=""
      style={{ zIndex: 3600 }}>
      <aside ref={dialogRef} className="hub-drawer" role="dialog" aria-modal="true"
        aria-labelledby="report-issue-title" aria-busy={busy} onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        onPaste={onPaste} data-testid="report-issue-modal"
        style={{ zIndex: 3601, whiteSpace: 'normal', top: 'var(--view-as-offset, 0px)' }}>
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <h3 id="report-issue-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>
            {t('title')}
          </h3>
          <button type="button" onClick={close} disabled={busy} aria-label={t('close')}
            title={busy ? t('tip.closeBusy') : t('close')} data-testid="report-issue-close"
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: busy ? 'not-allowed' : 'pointer',
              color: 'var(--c-text-muted-fg)', lineHeight: 1, opacity: busy ? 0.5 : 1, minWidth: 44, minHeight: 44 }}>
            ×
          </button>
        </div>

        {phase === 'done' ? (
          <div data-testid="report-issue-done">
            <p role="status" style={{ fontSize: 15 }}>{t('sent')}</p>
            {failed.length > 0 && (
              <p role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 14 }} data-testid="report-issue-partial">
                {t('partial', { names: failed.join(', ') })}
              </p>
            )}
            <button type="button" onClick={close} data-testid="report-issue-finish"
              style={{ background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
                padding: '10px 18px', fontSize: 14, cursor: 'pointer', minHeight: 44 }}>
              {t('close')}
            </button>
          </div>
        ) : (
          <>
            <p style={{ marginTop: 0, color: 'var(--c-text-muted-fg)', fontSize: 14 }}>{t('intro')}</p>
            <label htmlFor="report-issue-comment" style={{ display: 'block', fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
              {t('comment')}
            </label>
            <textarea id="report-issue-comment" data-testid="report-issue-comment" value={comment} rows={6}
              maxLength={MAX_COMMENT} disabled={busy} placeholder={t('commentPlaceholder')}
              onChange={(e) => setComment(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: 10, fontSize: 15, borderRadius: 6,
                border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-fg)',
                fontFamily: 'inherit', resize: 'vertical' }} />

            <div style={{ marginTop: 14 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                {t('screenshots', { max: MAX_SCREENSHOTS })}
              </div>
              <p style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--c-text-muted-fg)' }}>{t('pasteHint')}</p>
              {shots.length > 0 && (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 8px', display: 'flex', flexWrap: 'wrap', gap: 8 }}
                  data-testid="report-issue-shots">
                  {shots.map((s) => (
                    <li key={s.id} style={{ position: 'relative', width: 96 }}>
                      {s.url
                        ? <img src={s.url} alt={s.file.name || t('pasted')}
                            style={{ width: 96, height: 72, objectFit: 'cover', borderRadius: 6,
                              border: '1px solid var(--c-border)' }} />
                        : <div style={{ width: 96, height: 72, borderRadius: 6, border: '1px solid var(--c-border)' }} />}
                      <button type="button" disabled={busy} onClick={() => remove(s.id)}
                        aria-label={t('remove', { name: s.file.name || t('pasted') })}
                        title={t('remove', { name: s.file.name || t('pasted') })}
                        style={{ position: 'absolute', top: -8, insetInlineEnd: -8, width: 28, height: 28, borderRadius: 14,
                          border: '1px solid var(--c-border)', background: 'var(--c-surface)', color: 'var(--c-text-fg)',
                          cursor: 'pointer', lineHeight: 1 }}>
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <input ref={fileRef} type="file" accept={SCREENSHOT_TYPES.join(',')} multiple hidden onChange={onPick}
                data-testid="report-issue-file" />
              {shots.length < MAX_SCREENSHOTS && (
                <button type="button" disabled={busy} onClick={() => fileRef.current?.click()}
                  data-testid="report-issue-add" title={t('tip.add')}
                  style={{ background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)',
                    borderRadius: 6, padding: '8px 14px', fontSize: 14, cursor: 'pointer', minHeight: 44 }}>
                  {t('add')}
                </button>
              )}
              {problems.map((p) => (
                <p key={p} role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 13, margin: '6px 0 0' }}>{p}</p>
              ))}
            </div>

            <p style={{ fontSize: 12, color: 'var(--c-text-muted-fg)', margin: '14px 0', overflowWrap: 'anywhere' }}
              data-testid="report-issue-context">
              {context ? `${t('screen', { name: context })} · ` : ''}
              {t('context', { page: page || '—', version: BUILD_ID || '—' })}
              {environment ? ` · ${t('environment', { name: environment })}` : ''}
            </p>

            {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 14 }}>{error}</p>}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={send} disabled={!canSend} data-testid="report-issue-send"
                style={{ background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
                  padding: '10px 18px', fontSize: 14, cursor: canSend ? 'pointer' : 'not-allowed',
                  opacity: canSend ? 1 : 0.6, minHeight: 44 }}>
                {busy ? t('sending') : t('send')}
              </button>
              <button type="button" onClick={close} disabled={busy}
                style={{ background: 'none', border: '1px solid var(--c-border)', borderRadius: 6, padding: '10px 18px',
                  fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer', color: 'var(--c-text-fg)', minHeight: 44 }}>
                {t('cancel')}
              </button>
            </div>
          </>
        )}
      </aside>
    </div>
  );
}
